import type { Prisma } from "@productlane/db"
import type { HelpCenterArticleVersion, Schema } from "@productlane/zero"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { JSONValue, Zero } from "@rocicorp/zero"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"

interface RestoreArticleVersionArgs {
	zero: Zero<Schema>
	workspaceId: string
	articleId: string
	version: HelpCenterArticleVersion
	defaultLanguage: string
}

// Shared by the version-history dialog and the preview banner: both write the
// translation row, plus the base row when the version is in the default language.
export async function restoreArticleVersion({
	zero,
	workspaceId,
	articleId,
	version,
	defaultLanguage,
}: RestoreArticleVersionArgs): Promise<boolean> {
	// Resolve the row first; upserting a fresh id would leave two translation
	// rows for the same language behind.
	const existing = await zero.run(
		queries.helpCenterArticleTranslation({
			articleId,
			language: version.language,
		}),
	)
	const finalTranslationId = existing?.id ?? generateId()

	const fail = (error: unknown, action: string) => {
		console.error("Failed to restore version:", error)
		captureError(error, {
			domain: "general",
			action,
			workspaceId,
			extra: { articleId, versionId: version.id },
		})
		toast.error({
			title: "Restoring this version failed",
			description: "Try again in a moment.",
		})
	}

	const upsertResult = await zero.mutate(
		mutators.helpCenterArticleTranslations.upsert({
			id: finalTranslationId,
			workspaceId,
			language: version.language,
			helpCenterArticleId: articleId,
			title: version.title,
			summary: version.summary,
			content: version.content as string,
		}),
	).client

	if (upsertResult.type === "error") {
		fail(upsertResult.error, "restore version")
		return false
	}

	if (version.language === defaultLanguage) {
		const updateResult = await zero.mutate(
			mutators.helpCenterArticles.update({
				id: articleId,
				title: version.title,
				content: version.content as Prisma.JsonValue,
				imageUrl: version.imageUrl,
				imageBlurhash: version.imageBlurhash,
				summary: version.summary,
				icon: version.icon,
			}),
		).client

		if (updateResult.type === "error") {
			fail(updateResult.error, "update version")
			return false
		}
	}

	// The restore is itself a change, so it gets its own version row: without one
	// the history still points at the version that was just replaced, and the
	// restore cannot be undone.
	await zero.mutate(
		mutators.helpCenterArticles.createVersion({
			id: generateId(),
			articleId,
			title: version.title,
			content: version.content as JSONValue,
			imageUrl: version.imageUrl,
			imageBlurhash: version.imageBlurhash,
			summary: version.summary,
			icon: version.icon,
			language: version.language,
			forceNewVersion: true,
		}),
	).client

	toast.success({
		title: "Version restored",
		description: "The article has been restored to the selected version.",
	})

	analytics.track("restore-article-version", {
		articleId,
		versionId: version.id,
		language: version.language,
	})

	return true
}
